import CoreRolesCheckboxes from "@/components/CoreRolesCheckboxes.tsx";
import AssignCasesAutocomplete from "@/components/Forms/AssignCasesAutocomplete.tsx";
import AssignCasesDisplay from "@/components/Forms/AssignCasesDisplay.tsx";
import {
    addTaskToSelfAdHocTemplateSchema,
    addTaskToSelfAdHocTemplateWithCoreRoles,
} from "@/components/Forms/schema/addCaseTasksSchema.ts";
import { useAddCaseTaskToSelfAdHocTemplate } from "@/mutations/useAddCaseTaskToSelfAdHocTemplate.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 as Grid } from "@mui/material";
import dayjs from "dayjs";
import { RhfTextField } from "mui-rhf-integration";
import { RhfDatePicker } from "mui-rhf-integration/date-picker";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useMemo } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddTaskToSelfAdHocTemplateFormValues = z.infer<typeof addTaskToSelfAdHocTemplateSchema>;

type Props = {
    adminMode?: boolean;
    foundCaseId?: string;
    caseIdUUID?: string;
};

const AssignTasksToSelfAdHocForm = ({
    caseIdUUID,
    foundCaseId,
    adminMode = false,
}: Props): ReactNode => {
    const addTask = useAddCaseTaskToSelfAdHocTemplate(caseIdUUID);

    const form = useForm<AddTaskToSelfAdHocTemplateFormValues>({
        resolver: zodResolver(
            adminMode ? addTaskToSelfAdHocTemplateWithCoreRoles : addTaskToSelfAdHocTemplateSchema,
        ),
        defaultValues: {
            cases: [],
            duration: 0, // Default to 0 (no due date) unless user selects a date
        },
    });

    const due = form.watch("due");

    const differenceInDays = useMemo(() => {
        const now = dayjs();

        if (dayjs.isDayjs(due) && dayjs.isDayjs(now)) {
            return due.diff(now, "day") + 1;
        }

        // Return 0 if no due date is set (optional for tasks without due dates)
        return due ? 1 : 0;
    }, [due]);

    const handleSubmit = (values: AddTaskToSelfAdHocTemplateFormValues) => {
        addTask
            .mutateAsync({
                ...values,
                duration: differenceInDays,
            })
            .then(() => {
                enqueueSnackbar("Task added to case(s).", { variant: "success" });

                handleClose();
            })
            .catch(() => {
                enqueueSnackbar("Error: Add Task failed.", { variant: "error" });
            });
    };
    const handleClose = () => {
        form.reset({});
    };

    const onSubmit: SubmitHandler<AddTaskToSelfAdHocTemplateFormValues> = async (
        data: AddTaskToSelfAdHocTemplateFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    return (
        <FormProvider {...form}>
            <form>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesAutocomplete
                            limitToCurrentEmployee={!adminMode}
                            foundCaseId={foundCaseId}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesDisplay />
                    </Grid>
                    <Grid size={{ xs: 6 }} sx={{ pt: 1, py: 2 }}>
                        <RhfTextField
                            control={form.control}
                            name={"adHoc"}
                            label={"Ad Hoc Task"}
                            sx={{
                                width: "100%",
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <RhfDatePicker
                            control={form.control}
                            name={"due"}
                            label={"Due Date"}
                            views={["year", "month", "day"]}
                            sx={{
                                width: "100%",
                            }}
                        />
                    </Grid>
                    {adminMode && (
                        <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                            <CoreRolesCheckboxes />
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }} sx={{ pt: 1, textAlign: "center" }}>
                        <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                            Assign Tasks
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </FormProvider>
    );
};

export default AssignTasksToSelfAdHocForm;
